/**
 * Marketing & Growth Intelligence Landing Page
 *
 * 36th Sector Business - Self-Marketing Engine
 * MONETIZED - Not free for anyone
 */

import { useState } from 'react';
import { PlatformShell } from '../../layout/PlatformShell';
import { AccountMenu } from '../../layout/AccountMenu';

interface AnchorMarketingGrowthLandingProps {
  onNavigate: (view: string) => void;
  onAuthSuccess?: () => void;
}

const seoFeatures = [
  {
    icon: '🔍',
    title: 'Keyword Tracking',
    desc: 'Real-time keyword ranking monitoring across search engines',
  },
  {
    icon: '📝',
    title: 'Content Optimization',
    desc: 'AI-powered content suggestions for better rankings',
  },
  {
    icon: '📊',
    title: 'Ranking Analytics',
    desc: 'Comprehensive SERP tracking and competitor analysis',
  },
  { icon: '🔗', title: 'Backlink Analysis', desc: 'Monitor and optimize your backlink profile' },
  {
    icon: '⚡',
    title: 'Technical SEO',
    desc: 'Site speed, crawlability, and technical health checks',
  },
  {
    icon: '🎯',
    title: 'Local SEO',
    desc: 'Google Business Profile optimization and local rankings',
  },
];

const geoFeatures = [
  {
    icon: '🌍',
    title: 'Geographic Targeting',
    desc: 'Location-based search optimization for local markets',
  },
  { icon: '📍', title: 'Local Search', desc: 'Optimize for local search queries and maps' },
  {
    icon: '🗺️',
    title: 'Regional Rankings',
    desc: 'Track rankings across different geographic regions',
  },
  { icon: '🌐', title: 'Multi-Location', desc: 'Manage SEO across multiple business locations' },
  { icon: '🏪', title: 'Local Citations', desc: 'Build and manage local business citations' },
  { icon: '📱', title: 'Mobile Local', desc: 'Optimize for mobile local search intent' },
];

const bdFeatures = [
  { icon: '🎯', title: 'Lead Generation', desc: 'AI-powered lead capture and qualification' },
  {
    icon: '📈',
    title: 'Conversion Optimization',
    desc: 'A/B testing and conversion rate optimization',
  },
  { icon: '👥', title: 'Audience Targeting', desc: 'Precise audience segmentation and targeting' },
  { icon: '📧', title: 'Email Campaigns', desc: 'Automated email marketing workflows' },
  { icon: '📱', title: 'SMS Marketing', desc: 'SMS campaigns and notifications' },
  { icon: '🤝', title: 'CRM Integration', desc: 'Seamless integration with popular CRM systems' },
];

const analyticsFeatures = [
  {
    icon: '📊',
    title: 'Campaign Performance',
    desc: 'Real-time tracking of all marketing campaigns',
  },
  { icon: '💰', title: 'ROI Tracking', desc: 'Measure return on investment across all channels' },
  { icon: '📉', title: 'Predictive Insights', desc: 'AI-powered forecasting and trend analysis' },
  { icon: '🎯', title: 'Attribution', desc: 'Multi-touch attribution modeling' },
  { icon: '📊', title: 'Custom Dashboards', desc: 'Build personalized analytics dashboards' },
  { icon: '📈', title: 'Growth Metrics', desc: 'Track KPIs and growth metrics in real-time' },
];

const marketingPricingTiers = [
  {
    name: 'Sentinel',
    price: '$299/mo',
    features: ['5 Keywords tracked', '10 Pages optimized', 'Basic analytics', 'Email support'],
    highlight: false,
  },
  {
    name: 'Sovereign',
    price: '$799/mo',
    features: [
      '50 Keywords tracked',
      '50 Pages optimized',
      'Advanced analytics',
      'Priority support',
      'GEO targeting',
      'Lead generation',
    ],
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    features: [
      'Unlimited keywords',
      'Unlimited pages',
      'Custom analytics',
      '24/7 support',
      'API access',
      'Dedicated manager',
    ],
    highlight: false,
  },
];

const tazzieCapabilities = [
  {
    category: 'Sovereign Intelligence',
    features: [
      '37-Sector Intelligence',
      'Predictive Modelling',
      'Anomaly Detection',
      'Threat Assessment',
      'Outcome Stabilisation',
      'Cross-Sector Correlation',
    ],
  },
  {
    category: 'Voice & Interaction',
    features: [
      'Voice Input (Speech-to-Text)',
      'Streaming Responses',
      'Multi-Turn Dialogue',
      'Conversation Persistence',
      'Context Awareness',
      'Export & Share',
    ],
  },
  {
    category: 'Vision & Analysis',
    features: [
      'Image Analysis',
      'Multi-Modal Reasoning',
      'Document Analysis',
      'Data Visualisation',
      'Pattern Recognition',
      'Object Detection',
    ],
  },
  {
    category: 'Intelligence',
    features: [
      'Advanced Reasoning',
      'Predictive Modeling',
      'Real-Time Data',
      '37-Sector Intelligence',
      'Pattern Recognition',
      'Cross-Sector Correlation',
    ],
  },
  {
    category: 'Creative',
    features: [
      'Image Generation',
      'Concept Art',
      'Logo Design',
      'UI/UX Design',
      'Brand Identity',
      'Visual Content',
    ],
  },
  {
    category: 'Security',
    features: [
      'Threat Detection',
      'Cyber Defense',
      'Anomaly Mapping',
      'Security Alerts',
      'Fraud Detection',
      'Physical Security',
    ],
  },
  {
    category: 'Development',
    features: [
      'Code Generation',
      'Debugging',
      'Documentation',
      'Testing',
      'DevOps Automation',
      'API Development',
    ],
  },
  {
    category: 'Evolution',
    features: [
      'Self-Learning',
      'Self-Correcting',
      'Future-Operating',
      'Continuous Optimization',
      'Autonomous Decision-Making',
      'Self-Improving AI',
    ],
  },
];

const tazzieTiers = [
  {
    name: 'Citizen',
    price: '$0/mo',
    desc: 'Verified students — full power',
    verification: '.edu, .ac, GitHub',
    functions: [
      '∞ Unlimited messages — NO caps',
      'Real-time sovereign search',
      'Image & vision analysis',
      'Advanced code generation',
      'Safe search built-in',
      'Policy-locked deployment',
      'All 177 evolution engines',
    ],
    highlight: false,
    button: 'Verify Student',
  },
  {
    name: 'Sentinel',
    price: '$9.99/mo',
    desc: 'For individuals',
    functions: [
      '∞ Unlimited messages — NO caps',
      'Priority sovereign speed',
      'Advanced code + debugging',
      'Custom security policies',
      '5K API calls/mo',
      '100K token context',
      'Priority support',
    ],
    highlight: false,
    button: 'Get Started',
  },
  {
    name: 'Sovereign',
    price: '$24.99/mo',
    desc: 'For professionals',
    functions: [
      '∞ Unlimited messages — NO caps',
      'Voice input + streaming',
      'Priority model access',
      '50K API calls/mo',
      '5 team seats included',
      '24/7 sovereign support',
      'All multi-modal features',
    ],
    highlight: true,
    button: 'Go Sovereign',
  },
  {
    name: 'Commander',
    price: '$49.99/mo',
    desc: 'Per user — team leadership',
    functions: [
      '∞ Unlimited everything',
      'Unlimited API calls',
      'Unlimited team seats',
      'SSO + admin controls',
      'Shared knowledge base',
      'Dedicated account manager',
      'Cross-sector intelligence',
    ],
    highlight: false,
    button: 'Start Trial',
  },
  {
    name: 'Institutional',
    price: 'Custom',
    desc: 'Government & enterprise',
    functions: [
      '∞ Unlimited everything',
      'On-premise deployment',
      'Custom model training',
      'SLA 99.99% uptime',
      'Dedicated infrastructure',
      'White-glove support',
      'Air-gapped options',
    ],
    highlight: false,
    button: 'Contact Sales',
  },
];

export default function AnchorMarketingGrowthLanding({
  onNavigate,
}: AnchorMarketingGrowthLandingProps) {
  const [activeTab, setActiveTab] = useState<string>('seo');

  return (
    <PlatformShell
      onNavigate={onNavigate}
      backView="ecosystem"
      backLabel="Ecosystem"
      title="Marketing & Growth"
      extras={[
        { label: 'TAZZIE Chat', view: 'tazziedevil-chat' },
        { label: 'TAZZIE Network', view: 'tazzie-network' },
        { label: 'Marketing Command', view: 'tazziedevil-marketing' },
        { label: 'TAZZIEDEVIL', view: 'tazziedevil' },
      ]}
      mainId="anchor-marketing-growth-main"
      ariaLabel="Marketing & Growth — main content"
      mainClassName="bg-gradient-to-br from-amber-950 via-orange-950 to-yellow-950 text-white"
      navChildren={<AccountMenu onNavigate={onNavigate} />}
    >
      {/* Hero */}
      <section className="pt-10 pb-20 px-6 sm:pt-12">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-full px-4 py-2 mb-6">
            <span className="text-amber-400 text-sm font-bold">MONETIZED BUSINESS</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6">
            <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-yellow-300 bg-clip-text text-transparent">
              Marketing & Growth
            </span>
            <br />
            <span className="text-white">Intelligence Engine</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-10">
            Turn your internal SEO, GEO, and business development engines into a revenue-generating
            machine.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() =>
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-8 py-4 rounded-xl font-bold bg-gradient-to-r from-amber-500 to-orange-600 text-white"
            >
              Get Started
            </button>
            <button
              onClick={() =>
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-8 py-4 rounded-xl font-bold border border-amber-500/40 text-amber-300"
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

      {/* Features Tabs */}
      <section id="features" className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {[
              { key: 'seo', label: 'SEO Optimization', icon: '🔍' },
              { key: 'geo', label: 'GEO Optimization', icon: '🌍' },
              { key: 'bd', label: 'Business Dev', icon: '🎯' },
              { key: 'analytics', label: 'Analytics', icon: '📊' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 rounded-xl font-semibold ${activeTab === tab.key ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' : 'bg-white/5 border border-white/10 text-white/70'}`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'seo' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {seoFeatures.map((f, i) => (
                <div key={i} className="bg-black/40 border border-amber-500/20 rounded-2xl p-6">
                  <span className="text-3xl mb-4 block">{f.icon}</span>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-white/60 text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'geo' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {geoFeatures.map((f, i) => (
                <div key={i} className="bg-black/40 border border-orange-500/20 rounded-2xl p-6">
                  <span className="text-3xl mb-4 block">{f.icon}</span>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-white/60 text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'bd' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bdFeatures.map((f, i) => (
                <div key={i} className="bg-black/40 border border-yellow-500/20 rounded-2xl p-6">
                  <span className="text-3xl mb-4 block">{f.icon}</span>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-white/60 text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
          )}
          {activeTab === 'analytics' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {analyticsFeatures.map((f, i) => (
                <div key={i} className="bg-black/40 border border-amber-500/20 rounded-2xl p-6">
                  <span className="text-3xl mb-4 block">{f.icon}</span>
                  <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                  <p className="text-white/60 text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Marketing Pricing */}
      <section id="pricing" className="py-20 px-6 bg-black/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
              Marketing & Growth Pricing
            </span>
          </h2>
          <p className="text-white/60 text-center mb-12">
            Premium monetized business - no free access.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {marketingPricingTiers.map((tier, i) => (
              <div
                key={i}
                className={`rounded-2xl p-8 ${tier.highlight ? 'bg-gradient-to-b from-amber-500/20 to-black border-2 border-amber-500/40' : 'bg-black/40 border border-white/10'}`}
              >
                <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                <p className="text-4xl font-black text-amber-400 mb-2">{tier.price}</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-white/80 text-sm">
                      <span className="text-emerald-400">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => {
                    const backendTier = i === 0 ? 'STARTER' : i === 1 ? 'PRO' : 'ENTERPRISE';
                    localStorage.setItem('anchor_selected_plan', backendTier);
                    onNavigate?.('pricing');
                  }}
                  className={`w-full py-3 rounded-xl font-semibold ${tier.highlight ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white' : 'bg-white/10 text-white'}`}
                >
                  Subscribe Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAZZIEDEVIL2.0 - SEPARATE BUSINESS */}
      <section className="py-20 px-6 bg-gradient-to-b from-emerald-950/30 via-cyan-950/20 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-emerald-400 text-sm font-bold uppercase tracking-wider mb-2">
              ⚠️ SEPARATE BUSINESS ENTITY ⚠️
            </p>
            <h2 className="text-4xl font-bold text-white mb-4">TAZZIEDEVIL2.0 AI Chatbot</h2>
            <p className="text-white/60 max-w-3xl mx-auto">
              DREADCORE SYMPHONIC Intelligence - The hallmark of our business. AI done properly.
            </p>
            <p className="text-cyan-300 font-semibold mt-4">
              This is NOT the same as Marketing & Growth - This is our CORE AI Business
            </p>
          </div>

          {/* Capabilities */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-center text-white mb-8">
              TAZZIEDEVIL2.0 Full Capabilities
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tazzieCapabilities.map((cat, i) => (
                <div key={i} className="bg-black/40 border border-emerald-500/20 rounded-xl p-4">
                  <h4 className="text-emerald-400 font-bold text-sm mb-3">{cat.category}</h4>
                  <ul className="space-y-1">
                    {cat.features.map((f, j) => (
                      <li key={j} className="text-white/70 text-xs">
                        • {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Tier System */}
          <h3 className="text-2xl font-bold text-center text-white mb-8">
            TAZZIEDEVIL2.0 Pricing - More for Less
          </h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {tazzieTiers.map((tier, i) => (
              <div
                key={i}
                className={`rounded-2xl p-5 ${tier.highlight ? 'bg-gradient-to-b from-emerald-500/20 to-black border-2 border-emerald-500/40' : 'bg-black/40 border border-white/10'}`}
              >
                <div className="text-center mb-3">
                  <h4 className="text-lg font-bold text-white">{tier.name}</h4>
                  <p className="text-2xl font-black text-emerald-400">{tier.price}</p>
                  <p className="text-white/60 text-xs">{tier.desc}</p>
                </div>
                {tier.verification && (
                  <div className="mb-3 p-2 bg-amber-500/10 rounded-lg text-center">
                    <p className="text-amber-400 text-xs font-semibold">📧 {tier.verification}</p>
                  </div>
                )}
                <ul className="space-y-1 mb-4 max-h-32 overflow-y-auto">
                  {tier.functions.map((f, j) => (
                    <li key={j} className="text-white/70 text-xs">
                      ✓ {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onNavigate(tier.price === '$0/mo' ? 'tazziedevil' : 'pricing')}
                  className={`w-full py-2 rounded-xl text-sm font-semibold ${tier.highlight ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white' : 'bg-white/10 text-white'}`}
                >
                  {tier.button}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="text-white/50 text-sm">
              Student verification: .edu, .ac, GitHub Student Pack
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Marketing?</h2>
          <p className="text-white/70 mb-8">Premium monetized service - no free access.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() =>
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="px-8 py-4 rounded-xl font-bold bg-gradient-to-r from-amber-500 to-orange-600"
            >
              Choose Your Plan
            </button>
            <button
              onClick={() => onNavigate('ecosystem')}
              className="px-8 py-4 rounded-xl font-bold border border-amber-500/40 text-amber-300"
            >
              View All Sectors
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-amber-400">📈</span>
            <p className="text-white/70 text-sm">© 2026 Marketing & Growth Intelligence</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <span>Part of DREADCORE SI</span>
          </div>
        </div>
      </footer>
    </PlatformShell>
  );
}
